import { Fragment, useMemo } from 'react';
import { Box } from '@apia/theme';
import { TApiaTreePossibleValue } from '../../../../../types';
import { arrayOrArray } from '../../../../../utils/util';

type TFlattenedValues = (TApiaTreePossibleValue & { parent: string })[];

const RenderLevel = ({
  flattenedValues,
  childrenOf = 'root',
}: {
  flattenedValues: TFlattenedValues;
  childrenOf?: string;
}) => {
  const currentNodes = useMemo(
    () => flattenedValues.filter((current) => current.parent === childrenOf),
    [childrenOf, flattenedValues],
  );

  const sx = useMemo(() => ({ pl: 5 }), []);

  if (!currentNodes.length) return null;

  return (
    <Box as="ul" sx={sx}>
      {currentNodes.map((current) => (
        <Fragment key={current.value}>
          <Box as="li">{current.text}</Box>
          <RenderLevel
            flattenedValues={flattenedValues}
            childrenOf={current.value}
          />
        </Fragment>
      ))}
    </Box>
  );
};

function checkIfIsChild(
  value: string[],
  possibleValue: TApiaTreePossibleValue,
) {
  if (value.includes(possibleValue.value)) return true;

  const children = arrayOrArray(possibleValue.possibleValue);

  for (const child of children) {
    const isChild = checkIfIsChild(value, child);
    if (isChild === true) return true;
  }

  return false;
}

/**
 * Devuelve un ReactNode que representa todos los valores seleccionados con su
 * indentación correspondiente
 */
export default function GetTextValue({
  value,
  possibleValues,
}: {
  value: string[];
  possibleValues: TApiaTreePossibleValue[];
}) {
  const flattenedValues: TFlattenedValues = [];

  (function appendValues(
    valueToAppend: TApiaTreePossibleValue[],
    parent = 'root',
  ) {
    valueToAppend.forEach((current) => {
      if (!checkIfIsChild(value, current)) return;

      flattenedValues.push({ ...current, parent });
      appendValues(arrayOrArray(current.possibleValue), current.value);
    });
  })(possibleValues);

  return (
    <Box
      {...window.getVariant('layout.execution.form.fields.tree.readonlyTree')}
    >
      <RenderLevel flattenedValues={flattenedValues} />
    </Box>
  );
}

/**
 * Escribe los valores del tree en formato:
 *
 * Padre > Hijo
 * Padre2 > Padre3 > Hijo2
 * ...
 */
export function getTextValueShowingAllPaths(
  value: string[],
  possibleValues: TApiaTreePossibleValue[],
): string[] {
  const flattenedValues: Record<
    string,
    TApiaTreePossibleValue & { parent: string }
  > = {};

  function appendValues(
    valueToAppend: TApiaTreePossibleValue[],
    parent = 'root',
  ) {
    valueToAppend.forEach((current) => {
      flattenedValues[current.value] = { ...current, parent };
      appendValues(arrayOrArray(current.possibleValue), current.value);
    });
  }

  appendValues(possibleValues);

  function getPath(current: string): string[] {
    if (current === 'root') return [];

    const possibleValue = flattenedValues[current];
    return [...getPath(possibleValue.parent), possibleValue.text];
  }

  return value.map((current) => getPath(current).join(' > '));
}
